// LUMINA · pages/vajra/DharmaPathView.jsx
// Phase 59D: 修行次第树页面
// Routes: /vajra/dharma-path/:tradition (public) · /vajra/my-path (logged-in)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, GD, GDL, BG, TXT, TXT2 } from './constants';

const TRADITION_LABELS = {
  gelug:         { zh: "格鲁道次第",   en: "Gelug Lamrim" },
  nyingma:       { zh: "宁玛九乘",     en: "Nyingma Nine Yanas" },
  drikung_kagyu: { zh: "止贡那若六法", en: "Drikung Kagyu" },
  sakya:         { zh: "萨迦道果",     en: "Sakya Lamdre" },
  jonang:        { zh: "觉囊时轮",     en: "Jonang Kalachakra" },
};

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: TXT2, marginBottom: 6 }}>
        <span>修行进度</span>
        <span>{completed}/{total} 节点已圆满 · {pct}%</span>
      </div>
      <div style={{ height: 6, background: "rgba(139,105,20,0.12)", borderRadius: 3 }}>
        <div style={{
          height: 6, borderRadius: 3, transition: "width .4s",
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${GD}, #A07828)`,
        }}/>
      </div>
    </div>
  );
}

function NodeRow({ node, depth, token, onProgressUpdate }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const [updating, setUpdating] = useState(false);

  const prog = node.my_progress;
  const status = prog?.status || "not_started";
  const locked = node.locked;
  const hasChildren = node.children && node.children.length > 0;

  let statusIcon, statusLabel;
  if (locked) {
    statusIcon = "🔑";
    statusLabel = `需灌顶: ${node.credential_key}`;
  } else if (status === "completed") {
    statusIcon = "✅"; statusLabel = "已圆满";
  } else if (status === "in_progress") {
    statusIcon = "⏳"; statusLabel = "进行中";
  } else {
    statusIcon = "🔒"; statusLabel = "未开始";
  }

  const indent = depth * 20;

  const updateProgress = async (newStatus) => {
    if (!token || locked) return;
    setUpdating(true);
    try {
      await fetch(`${API}/vajra/my-path/progress/${node.id}`, {
        method: "PATCH",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      onProgressUpdate();
    } catch {}
    setUpdating(false);
  };

  return (
    <div>
      <div
        onClick={() => !locked && setExpanded(e => !e)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: `8px 12px 8px ${12 + indent}px`,
          cursor: locked ? "default" : "pointer",
          borderRadius: 8,
          background: expanded && hasChildren ? "rgba(139,105,20,0.04)" : "transparent",
          transition: "background .15s",
        }}
      >
        <span style={{ fontSize: 16, minWidth: 24 }}>{statusIcon}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 14,
            color: locked ? TXT2 : TXT,
            fontWeight: depth === 0 ? 600 : 400,
          }}>
            {node.name_zh}
          </div>
          {node.name_en && (
            <div style={{ fontSize: 10, color: TXT2, opacity: 0.6, letterSpacing: 0.5 }}>
              {node.name_en}
            </div>
          )}
        </div>
        {token && !locked && (
          <select
            value={status}
            onChange={e => { e.stopPropagation(); updateProgress(e.target.value); }}
            onClick={e => e.stopPropagation()}
            disabled={updating}
            style={{
              fontSize: 10, padding: "2px 6px", borderRadius: 4,
              border: `1px solid rgba(139,105,20,0.25)`,
              background: "#fff", color: TXT2, cursor: "pointer",
            }}
          >
            <option value="not_started">未开始</option>
            <option value="in_progress">进行中</option>
            <option value="completed">已圆满</option>
          </select>
        )}
        {hasChildren && (
          <span style={{ color: GD, fontSize: 12, opacity: 0.6 }}>
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>

      {expanded && node.description_zh && !hasChildren && (
        <div style={{
          marginLeft: 12 + indent + 34, marginBottom: 4,
          fontSize: 11, color: TXT2, lineHeight: 1.6, opacity: 0.8,
        }}>
          {node.description_zh}
        </div>
      )}

      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <NodeRow
              key={child.id} node={child} depth={depth + 1}
              token={token} onProgressUpdate={onProgressUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DharmaPathView({ isMyPath = false }) {
  const { tradition } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("vajra_token") || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noTradition, setNoTradition] = useState(false);
  const [traditions, setTraditions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const headers = token ? { "Authorization": `Bearer ${token}` } : {};
    const url = isMyPath
      ? `${API}/vajra/my-path`
      : `${API}/vajra/dharma-path/${tradition}`;
    try {
      const r = await fetch(url, { headers });
      if (!r.ok) { setLoading(false); return; }
      const d = await r.json();
      if (d.has_tradition === false) {
        setNoTradition(true);
        setTraditions(d.traditions || []);
      } else {
        setData(d);
        setNoTradition(false);
      }
    } catch {}
    setLoading(false);
  }, [tradition, isMyPath, token, API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const countNodes = (nodes) =>
    (nodes || []).reduce((acc, n) => acc + 1 + countNodes(n.children || []), 0);
  const countCompleted = (nodes) =>
    (nodes || []).reduce((acc, n) => {
      const done = n.my_progress?.status === "completed" ? 1 : 0;
      return acc + done + countCompleted(n.children || []);
    }, 0);

  const selectTradition = async (key) => {
    if (!token) { navigate(`/vajra/dharma-path/${key}`); return; }
    try {
      const r = await fetch(`${API}/vajra/me/primary-tradition`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tradition: key }),
      });
      if (r.ok) { setNoTradition(false); load(); }
    } catch {}
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center",
      justifyContent: "center", color: TXT2, fontSize: 13, letterSpacing: 2,
      fontFamily: "'Noto Serif SC',serif" }}>
      载入中…
    </div>
  );

  // Not logged in — show login prompt for /my-path
  if (isMyPath && !token) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", fontFamily: "'Noto Serif SC',serif", gap: 16 }}>
      <div style={{ fontSize: 18, color: GD, letterSpacing: 2 }}>修行次第</div>
      <div style={{ fontSize: 13, color: TXT2 }}>登录后查看您的个人修行进度与次第导航</div>
      <a href="/vajra" style={{
        background: `linear-gradient(135deg,${GD},#A07828)`, border: "none", borderRadius: 8,
        color: "#fff", fontSize: 13, padding: "8px 28px", cursor: "pointer",
        textDecoration: "none", letterSpacing: 1,
      }}>前往登录 →</a>
    </div>
  );

  if (noTradition) {
    const TRADITION_LIST = [
      { key: "gelug",         zh: "格鲁道次第",   en: "Gelug Lamrim" },
      { key: "nyingma",       zh: "宁玛九乘",     en: "Nyingma Nine Yanas" },
      { key: "drikung_kagyu", zh: "止贡那若六法", en: "Drikung Kagyu Naropa" },
      { key: "sakya",         zh: "萨迦道果",     en: "Sakya Lamdre" },
      { key: "jonang",        zh: "觉囊时轮",     en: "Jonang Kalachakra" },
    ];
    return (
      <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Noto Serif SC',serif", color: TXT }}>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "48px 24px" }}>
          <a href="/vajra" style={{ background: "none", border: "none",
            color: GD, cursor: "pointer", fontSize: 12, marginBottom: 32, textDecoration: "none", display: "inline-block" }}>
            如意宝金刚社群
          </a>
          <div style={{ fontSize: 22, color: GD, letterSpacing: 3, marginBottom: 8 }}>选择主修传承</div>
          <div style={{ fontSize: 12, color: TXT2, marginBottom: 32 }}>
            选择后您的修行次第将按此传承展开，随时可在设置中更改
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {TRADITION_LIST.map(t => (
              <div key={t.key} onClick={() => selectTradition(t.key)} style={{
                padding: "18px 20px", borderRadius: 12, cursor: "pointer",
                background: "#fff",
                border: `1px solid rgba(212,175,55,0.15)`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                transition: "border-color .15s, box-shadow .15s",
              }}
                onMouseOver={e => { e.currentTarget.style.borderColor = GD; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(212,175,55,0.15)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
              >
                <div style={{ fontSize: 16, color: GD, fontWeight: 600 }}>{t.zh}</div>
                <div style={{ fontSize: 11, color: TXT2, marginTop: 4 }}>{t.en}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center",
      justifyContent: "center", color: TXT2, fontSize: 13, fontFamily: "'Noto Serif SC',serif" }}>
      传承不存在或加载失败
    </div>
  );

  const trad = TRADITION_LABELS[data.tradition] || { zh: data.name_zh, en: data.name_en };
  const total = countNodes(data.tree);
  const completed = countCompleted(data.tree);

  return (
    <div style={{ minHeight: "100vh", background: BG,
      fontFamily: "'Noto Serif SC',serif", color: TXT }}>

      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(253,251,247,0.95)", backdropFilter: "blur(16px)",
        borderBottom: `1px solid rgba(212,175,55,0.2)`,
        padding: "0 24px", height: 52,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <a href="/vajra" style={{ background: "none", border: "none",
          color: GD, cursor: "pointer", fontSize: 13, textDecoration: "none" }}>如意宝金刚社群</a>
        <div style={{ flex: 1 }}>
          <span style={{ color: GD, fontSize: 15, fontWeight: 600 }}>{trad.zh}</span>
          {trad.en && <span style={{ color: TXT2, fontSize: 10, marginLeft: 8, opacity: 0.6 }}>{trad.en}</span>}
        </div>
        <button onClick={() => navigate("/vajra")} style={{
          background: "none", border: `1px solid rgba(139,105,20,0.3)`,
          borderRadius: 6, color: GD, fontSize: 11, padding: "4px 12px", cursor: "pointer",
        }}>
          教法图书馆 →
        </button>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 24px 80px" }}>
        {token && <ProgressBar completed={completed} total={total} />}

        <div style={{
          background: "#fff", borderRadius: 12,
          border: `1px solid rgba(212,175,55,0.15)`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}>
          {data.tree.map(node => (
            <NodeRow
              key={node.id} node={node} depth={0}
              token={token} onProgressUpdate={load}
            />
          ))}
          {data.tree.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: TXT2, fontSize: 12 }}>
              次第内容正在整理中，敬请期待
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
